// src/components/acciones/CuponesPreview.js
import React, { memo, useRef, useState, useEffect, useCallback, useMemo } from 'react';

// Constante `BOLETA_BLUE` que centraliza un valor de configuración compartido.
const BOLETA_BLUE = '#2b3990';
// Constante `SVG_BASE` que centraliza un valor de configuración compartido.
const SVG_BASE     = process.env.PUBLIC_URL + '/designs';
// Constante `CUPONES_BASE` que centraliza un valor de configuración compartido.
const CUPONES_BASE = `${SVG_BASE}/cupones`;

// ============================================
// MAPEO LOGO NUMBER → ÍCONO SVG DE CUPÓN
// ============================================
const LOGO_TO_CUPON_ICON = {
  1: '1_C.svg',
  2: '23_C.svg',
  3: '23_C.svg',
  4: '4_C.svg',
  5: '5_C.svg',
};

// Función `resolveIconSvgUrl` que calcula el resultado necesario para el flujo.
const resolveIconSvgUrl = (selectedLogoNumber) => {
  // Valor intermedio `num` utilizado por la lógica de este bloque.
  const num = Number(selectedLogoNumber);
  if (!num || num < 1 || num > 5) return null;
  // Valor intermedio `fileName` utilizado por la lógica de este bloque.
  const fileName = LOGO_TO_CUPON_ICON[num];
  return fileName ? `${CUPONES_BASE}/${fileName}` : null;
};

// ============================================
// CONFIGURACIÓN DEL LIENZO
// ============================================
const CUPONES_CONFIG = {
  vertical: {
    pageWidth:    900,
    pageHeight:   500,
    cuponWidth:   760,
    cuponHeight:  360,
    headerHeight: 60,
    footerHeight: 28,
    fontSize: { sheetTitle: 12, cuponLabel: 22, company: 14, sheetFooter: 8 },
  },
  horizontal: {
    pageWidth:    900,
    pageHeight:   500,
    cuponWidth:   760,
    cuponHeight:  360,
    headerHeight: 60,
    footerHeight: 28,
    fontSize: { sheetTitle: 12, cuponLabel: 22, company: 14, sheetFooter: 8 },
  },
};

// Constante `MARCO_KEYWORD_TO_VARIANT` que centraliza un valor de configuración compartido.
const MARCO_KEYWORD_TO_VARIANT = [
  { keywords: ['celeste', 'sky', 'lightblue'],    code: 'CE', color: '#4ea3d6' },
  { keywords: ['azul', 'blue'],                   code: 'A',  color: '#2b3990' },
  { keywords: ['cafe', 'café', 'brown', 'sepia'], code: 'C',  color: '#6b4a2b' },
  { keywords: ['rojo', 'red', 'burdeos', 'vino'], code: 'R',  color: '#a02828' },
  { keywords: ['verde', 'green', 'esmeralda'],    code: 'V',  color: '#2f7a3f' },
];

// Constante `DEFAULT_VARIANT` que centraliza un valor de configuración compartido.
const DEFAULT_VARIANT = { code: 'A', color: BOLETA_BLUE };

// Función `inferVariant` que calcula el resultado necesario para el flujo.
const inferVariant = (boletaData, designUrls) => {
  // Valor intermedio `designId` utilizado por la lógica de este bloque.
  const designId  = (boletaData?.designId   || '').toLowerCase();
  // Valor intermedio `marcoUrl` utilizado por la lógica de este bloque.
  const marcoUrl  = (designUrls?.marco      || '').toLowerCase();
  // Valor intermedio `cuponHint` utilizado por la lógica de este bloque.
  const cuponHint = (boletaData?.cuponColor || '').toLowerCase();
  // Valor intermedio `haystack` utilizado por la lógica de este bloque.
  const haystack  = `${cuponHint} ${designId} ${marcoUrl}`;

  for (const v of MARCO_KEYWORD_TO_VARIANT) {
    if (v.keywords.some(k => haystack.includes(k))) return { code: v.code, color: v.color };
  }
  return DEFAULT_VARIANT;
};

// Función `resolveCuponSvgUrl` que calcula el resultado necesario para el flujo.
const resolveCuponSvgUrl = (boletaData, variantCode, explicitNumber) => {
  // Valor intermedio `raw` utilizado por la lógica de este bloque.
  const raw     = explicitNumber ?? boletaData?.cuponDesignNumber;
  // Valor intermedio `num` utilizado por la lógica de este bloque.
  const num     = Number(raw) || 1;
  // Valor intermedio `safeNum` utilizado por la lógica de este bloque.
  const safeNum = Math.min(Math.max(num, 1), 5);
  return `${CUPONES_BASE}/${safeNum}_${variantCode}.svg`;
};

// Función `buildCuponSvgUrl` que construye la estructura o el valor requerido.
export const buildCuponSvgUrl = (designNumber, variantCode = 'A') => {
  // Valor intermedio `safeNum` utilizado por la lógica de este bloque.
  const safeNum = Math.min(Math.max(Number(designNumber) || 1, 1), 5);
  return `${CUPONES_BASE}/${safeNum}_${variantCode}.svg`;
};

// Función `inferCuponVariantCode` que calcula el resultado necesario para el flujo.
export const inferCuponVariantCode = (boletaData, designUrls) => inferVariant(boletaData, designUrls).code;

// ============================================
// CACHE + FETCH
// ============================================
const svgTextCache = new Map();

// Función `fetchSvgText` que obtiene la información necesaria para el flujo.
async function fetchSvgText(url) {
  if (svgTextCache.has(url)) return svgTextCache.get(url);
  // Valor intermedio `promise` utilizado por la lógica de este bloque.
  const promise = fetch(url).then(r => {
    if (!r.ok) throw new Error(`Failed to load ${url}`);
    return r.text();
  });
  svgTextCache.set(url, promise);
  try {
    // Valor intermedio `text` utilizado por la lógica de este bloque.
    const text = await promise;
    svgTextCache.set(url, text);
    return text;
  } catch (e) {
    svgTextCache.delete(url);
    throw e;
  }
}

// Valor intermedio `uidCounter` utilizado por la lógica de este bloque.
let uidCounter = 0;
// Función `nextUid` que encapsula una operación reutilizable de este módulo.
const nextUid = () => `cup${Date.now().toString(36)}${(uidCounter++).toString(36)}`;

/**
 * 🛠️ CAMBIO CLAVE: Función mejorada para asegurar que las máscaras y recortes 
 * no colisionen entre diferentes cupones.
 */
function makeSvgIdsUnique(svgText, prefix) {
  // 1. IDs principales (id="...")
  let result = svgText.replace(/\bid="([^"]+)"/g, (_m, id) => `id="${prefix}-${id}"`);
  
  // 2. Referencias url(#...) - Atrapa rellenos, máscaras y filtros
  result = result.replace(/url\(#([^)]+)\)/g, (_m, id) => `url(#${prefix}-${id})`);
  
  // 3. Referencias href y xlink:href (para símbolos e imágenes)
  result = result.replace(/xlink:href="#([^"]+)"/g, (_m, id) => `xlink:href="#${prefix}-${id}"`);
  result = result.replace(/(?<!xlink:)href="#([^"]+)"/g, (_m, id) => `href="#${prefix}-${id}"`);

  // 4. Atributos directos que a veces no usan la sintaxis url()
  const attrs = ['mask', 'clip-path', 'filter', 'fill', 'stroke'];
  attrs.forEach(attr => {
    // Valor intermedio `reg` utilizado por la lógica de este bloque.
    const reg = new RegExp(`\\b${attr}="([^"]+)"`, 'g');
    result = result.replace(reg, (match, val) => {
      if (val.startsWith('#')) return `${attr}="#${prefix}-${val.substring(1)}"`;
      return match;
    });
  });
  
  return result;
}

// ============================================
// COMPONENTE: RawSvg
// ============================================
const RawSvg = memo(function RawSvg({
  url,
  fallbackColor    = BOLETA_BLUE,
  fallbackText     = 'Diseño no disponible',
  showFallbackText = true,
  style            = {},
}) {
  // Referencia mutable `containerRef` que conserva datos entre renderizados sin provocar uno nuevo.
  const containerRef = useRef(null);
  // Estado local `status` y su actualizador, usados para reflejar cambios en la interfaz.
  const [status, setStatus] = useState('loading');

  useEffect(() => {
    // Valor intermedio `cancelled` utilizado por la lógica de este bloque.
    let cancelled = false;
    setStatus('loading');
    if (!url) { setStatus('error'); return; }

    (async () => {
      try {
        // Valor intermedio `svgText` utilizado por la lógica de este bloque.
        const svgText = await fetchSvgText(url);
        if (cancelled || !containerRef.current) return;

        // Valor intermedio `prefix` utilizado por la lógica de este bloque.
        const prefix = nextUid();
        // Valor intermedio `uniqueSvg` utilizado por la lógica de este bloque.
        const uniqueSvg = makeSvgIdsUnique(svgText, prefix);

        containerRef.current.innerHTML = uniqueSvg;
        // Valor intermedio `svgEl` utilizado por la lógica de este bloque.
        const svgEl = containerRef.current.querySelector('svg');

        if (!svgEl) { setStatus('error'); return; }

        svgEl.setAttribute('width',  '100%');
        svgEl.setAttribute('height', '100%');
        svgEl.setAttribute('preserveAspectRatio', 'xMidYMid meet');
        svgEl.style.display       = 'block';
        svgEl.style.overflow      = 'visible'; // 🛠️ Importante para evitar cortes internos
        svgEl.style.pointerEvents = 'none';
        svgEl.style.userSelect    = 'none';

        if (!cancelled) setStatus('ready');
      } catch (err) {
        if (!cancelled) setStatus('error');
      }
    })();
    return () => { cancelled = true; };
  }, [url]);

  if (status === 'error') {
    return (
      <div style={{
        width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center',
        border: `1px dashed ${fallbackColor}`, borderRadius: 4, color: fallbackColor,
        fontSize: 11, opacity: 0.6, ...style,
      }}>
        {showFallbackText ? fallbackText : '—'}
      </div>
    );
  }

  return (
    <div ref={containerRef} style={{
      width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center',
      userSelect: 'none', overflow: 'visible', ...style,
    }} />
  );
});

// ============================================
// COMPONENTE: CuponLarge
// ============================================
const CuponLarge = memo(function CuponLarge({ width, height, svgUrl, iconUrl, accentColor }) {
  return (
    <div style={{ width: `${width}px`, height: `${height}px`, position: 'relative', flexShrink: 0, overflow: 'visible' }}>
      <div style={{ position: 'absolute', inset: 0, zIndex: 1, overflow: 'visible' }}>
        <RawSvg url={svgUrl} fallbackColor={accentColor} />
      </div>
      {iconUrl && (
        <div style={{ position: 'absolute', inset: 0, zIndex: 2, pointerEvents: 'none', userSelect: 'none', overflow: 'visible' }}>
          <RawSvg url={iconUrl} fallbackColor={accentColor} showFallbackText={false} />
        </div>
      )}
    </div>
  );
});

// ============================================
// COMPONENTE PRINCIPAL: CuponesPreview
// ============================================
function CuponesPreview({
  boletaData, designUrls = {}, companyName = '', orientation = 'vertical',
  cuponDesignNumber, selectedLogoNumber,
}) {
  // Valor intermedio `config` utilizado por la lógica de este bloque.
  const config = CUPONES_CONFIG[orientation] || CUPONES_CONFIG.vertical;

  // Valor `propiedades` memoizado para recalcularlo solo cuando cambian sus dependencias.
  const { variant, svgUrl, iconUrl } = useMemo(() => {
    // Valor intermedio `v` utilizado por la lógica de este bloque.
    const v = inferVariant(boletaData, designUrls);
    // Valor intermedio `logoNum` utilizado por la lógica de este bloque.
    const logoNum = selectedLogoNumber ?? boletaData?.logoDesignNumber;
    return {
      variant: v,
      svgUrl:  resolveCuponSvgUrl(boletaData, v.code, cuponDesignNumber),
      iconUrl: resolveIconSvgUrl(logoNum),
    };
  }, [boletaData, designUrls, cuponDesignNumber, selectedLogoNumber]);

  // Valor intermedio `accentColor` utilizado por la lógica de este bloque.
  const accentColor = variant.color;

  return (
    <div className="protected-content" style={{
        position: 'relative', width: `${config.pageWidth}px`, height: `${config.pageHeight}px`,
        backgroundColor: '#fffef8', overflow: 'hidden', display: 'flex', flexDirection: 'column',
        backgroundImage: `radial-gradient(circle at 0% 0%, ${accentColor}06 0%, transparent 40%), radial-gradient(circle at 100% 100%, ${accentColor}06 0%, transparent 40%)`
      }}>
      <div style={{
          width: '100%', height: `${config.headerHeight}px`, display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center', borderBottom: `1px solid ${accentColor}30`,
          flexShrink: 0, padding: '0 16px', background: `linear-gradient(180deg, ${accentColor}08 0%, transparent 100%)`, gap: '2px',
        }}>
        <span style={{ fontSize: `${config.fontSize.cuponLabel}px`, fontFamily: '"Times New Roman", serif', color: accentColor, fontWeight: 700, letterSpacing: '4px', textTransform: 'uppercase' }}>CUPÓN</span>
        {companyName && (
          <span style={{ fontSize: `${config.fontSize.company}px`, fontFamily: '"Times New Roman", serif', color: `${accentColor}cc`, fontWeight: 600, letterSpacing: '2px', textTransform: 'uppercase', textAlign: 'center' }}>
            {companyName}
          </span>
        )}
      </div>

      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '12px 24px', overflow: 'visible' }}>
        <CuponLarge width={config.cuponWidth} height={config.cuponHeight} svgUrl={svgUrl} iconUrl={iconUrl} accentColor={accentColor} />
      </div>

      <div style={{ width: '100%', height: `${config.footerHeight}px`, display: 'flex', alignItems: 'center', justifyContent: 'center', borderTop: `1px dashed ${accentColor}40`, flexShrink: 0, background: `linear-gradient(0deg, ${accentColor}06 0%, transparent 100%)` }}>
        <span style={{ fontSize: `${config.fontSize.sheetFooter}px`, fontFamily: '"Times New Roman", serif', color: `${accentColor}88`, letterSpacing: '2px', textTransform: 'uppercase' }}>Diseño de cupón aplicado</span>
      </div>

      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: 100, backgroundColor: 'transparent' }} onContextMenu={(e) => e.preventDefault()} />
    </div>
  );
}

// ============================================
// EXPORTS
// ============================================
export const CuponesPreviewScaled = memo(function CuponesPreviewScaled({
  boletaData, designUrls, companyName, orientation = 'vertical',
  cuponDesignNumber, selectedLogoNumber, zoom = 1, fitMode = 'contain', className = '', style = {},
}) {
  // Referencia mutable `outerRef` que conserva datos entre renderizados sin provocar uno nuevo.
  const outerRef = useRef(null);
  // Estado local `containerSize` y su actualizador, usados para reflejar cambios en la interfaz.
  const [containerSize, setContainerSize] = useState({ w: 0, h: 0 });
  // Callback `measure` memoizado para conservar su referencia entre renderizados.
  const measure = useCallback(() => {
    if (!outerRef.current) return;
    // offsetWidth/offsetHeight no se ven afectados por el transform: scale()
    // del lienzo padre: así conserva un tamaño real y estable dentro de la distribución.
    const w = outerRef.current.offsetWidth;
    // Valor intermedio `h` utilizado por la lógica de este bloque.
    const h = outerRef.current.offsetHeight;
    setContainerSize({ w, h });
  }, []);

  useEffect(() => {
    measure();
    // Valor intermedio `ro` utilizado por la lógica de este bloque.
    const ro = new ResizeObserver(measure);
    if (outerRef.current) ro.observe(outerRef.current);
    return () => ro.disconnect();
  }, [measure]);

  // Configuración `canon` que agrupa valores relacionados.
  const canon = { width: 900, height: 500 };
  // Valor intermedio `computedScale` utilizado por la lógica de este bloque.
  let computedScale = 1;
  if (containerSize.w > 0) {
    computedScale = fitMode === 'width' ? containerSize.w / canon.width : Math.min(containerSize.w / canon.width, containerSize.h / canon.height);
  }
  computedScale *= zoom;

  return (
    <div ref={outerRef} className={className} style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', ...style }}>
      {containerSize.w > 0 && (
        <div style={{ width: canon.width * computedScale, height: canon.height * computedScale, position: 'relative' }}>
          <div style={{ width: canon.width, height: canon.height, transformOrigin: 'top left', transform: `scale(${computedScale})`, position: 'absolute', top: 0, left: 0 }}>
            <CuponesPreview boletaData={boletaData} designUrls={designUrls} companyName={companyName} orientation={orientation} cuponDesignNumber={cuponDesignNumber} selectedLogoNumber={selectedLogoNumber} />
          </div>
        </div>
      )}
    </div>
  );
});

// Valor intermedio `CuponThumbnail` utilizado por la lógica de este bloque.
export const CuponThumbnail = memo(({ designNumber, variantCode = 'A', url, style = {} }) => (
  <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', ...style }}>
    <RawSvg url={url || buildCuponSvgUrl(designNumber, variantCode)} showFallbackText={false} />
  </div>
));

export { RawSvg as CenteredSvg };
export default memo(CuponesPreview);
